import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { gsap } from '@/lib/gsap'
import BoardingPass from '@/components/landing/boarding-pass'
import DestinationTicker from '@/components/landing/destination-ticker'
import { GLOBAL_COPY } from '@/constants/aerive'

const ARCS = [
    { rx: 560, ry: 140, rotate: -14 },
    { rx: 520, ry: 196, rotate: 17 },
    { rx: 610, ry: 104, rotate: -33 }
]

// The finale: a giant word, the globe rising beneath it with gold flight paths, and a
// boarding pass for wherever you wish to go.
const GlobalSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const wordRef = useRef<HTMLHeadingElement>(null)
    const globeRef = useRef<HTMLDivElement>(null)
    const globeImageRef = useRef<HTMLDivElement>(null)
    const copyRef = useRef<HTMLDivElement>(null)
    const cardRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const section = sectionRef.current
        const stage = stageRef.current
        const globe = globeRef.current
        if (!section || !stage || !globe) return

        const arcs = Array.from(globe.querySelectorAll<SVGPathElement>('[data-arc]'))
        const dots = Array.from(globe.querySelectorAll<SVGCircleElement>('[data-arc-dot]'))
        const copyItems = copyRef.current?.querySelectorAll<HTMLElement>('[data-global-item]') ?? []

        const mm = gsap.matchMedia()

        mm.add({
            motion: '(prefers-reduced-motion: no-preference)',
            reduce: '(prefers-reduced-motion: reduce)'
        }, (context) => {
            const { motion } = context.conditions as { motion: boolean }

            const timeline = gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: () => `+=${Math.round(window.innerHeight * 2.2)}`,
                    scrub: 1,
                    pin: stage,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    refreshPriority: 5
                }
            })

            if (motion) {
                timeline
                    .fromTo(wordRef.current, { yPercent: 30, autoAlpha: 0.2 }, { yPercent: 0, autoAlpha: 1, ease: 'power2.out', duration: 2.2 }, 0)
                    .fromTo(globe, { y: () => window.innerHeight * 0.55, scale: 0.84 }, { y: 0, scale: 1, ease: 'power2.out', duration: 4 }, 0)
                    .fromTo(globeImageRef.current, { rotation: 10 }, { rotation: -6, duration: 7 }, 0)
                    .fromTo(arcs, { strokeDashoffset: 1 }, { strokeDashoffset: 0, stagger: 0.35, duration: 2.2 }, 2)
                    .fromTo(copyItems, { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, ease: 'power2.out', stagger: 0.15, duration: 1.3 }, 3)
                    .fromTo(cardRef.current, { autoAlpha: 0, y: 160, rotation: 8 }, { autoAlpha: 1, y: 0, rotation: -3, ease: 'power2.out', duration: 2.2 }, 3.6)

                dots.forEach((dot, index) => {
                    const path = arcs[index]
                    if (!path) return
                    const at = 2.3 + index * 0.35
                    timeline
                        .fromTo(dot, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 }, at)
                        .to(dot, {
                            motionPath: { path, align: path, alignOrigin: [0.5, 0.5] },
                            ease: 'power1.inOut',
                            duration: 3
                        }, at)
                })

                timeline.to({}, { duration: 1.2 })
            } else {
                timeline
                    .fromTo(copyItems, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.5 }, 1)
                    .fromTo(cardRef.current, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.5 }, 1.5)
                    .to({}, { duration: 1.5 })
            }
        })

        return () => mm.revert()
    }, [])

    return (
        <section id='global' ref={sectionRef} aria-labelledby='global-title' className='relative z-10 bg-obsidian text-ivory'>
            <div ref={stageRef} className='stage relative w-full overflow-hidden bg-obsidian'>
                <div className='stage-safe absolute inset-x-0 top-0'>
                    <div className='pointer-events-none absolute inset-0 bg-linear-to-b from-onyx via-obsidian to-obsidian' />

                    <h2
                        id='global-title'
                        ref={wordRef}
                        className='pointer-events-none absolute inset-x-0 top-[10%] select-none bg-linear-to-b from-champagne via-champagne/45 to-champagne/0 bg-clip-text pb-[0.08em] text-center font-heading text-[clamp(5.5rem,25vw,27rem)] font-light leading-[0.9] tracking-[-0.02em] text-transparent md:top-[5%]'
                    >
                        {GLOBAL_COPY.word}
                    </h2>

                    <div ref={globeRef} className='globe-box top-[34%] will-change-transform md:top-[27%]'>
                        <div ref={globeImageRef} className='absolute inset-0 overflow-hidden rounded-full'>
                            <Image
                                src='/images/aerive/globe.png'
                                alt='A dark globe with Europe, Africa and the Middle East traced in champagne gold'
                                fill
                                sizes='(max-width: 767px) 94vw, 76vh'
                                quality={85}
                                loading='eager'
                                className='scale-[1.06] object-cover'
                            />
                        </div>
                        <svg viewBox='0 0 1000 1000' aria-hidden='true' className='absolute inset-0 h-full w-full overflow-visible text-champagne'>
                            {ARCS.map((arc) => (
                                <g key={arc.rotate} transform={`rotate(${arc.rotate} 500 500)`}>
                                    <path
                                        data-arc
                                        d={`M ${500 - arc.rx} 500 A ${arc.rx} ${arc.ry} 0 0 0 ${500 + arc.rx} 500`}
                                        pathLength={1}
                                        fill='none'
                                        stroke='currentColor'
                                        strokeOpacity={0.55}
                                        strokeWidth={1.4}
                                        style={{ strokeDasharray: 1, strokeDashoffset: 0 }}
                                    />
                                    <circle data-arc-dot cx={500 - arc.rx} cy={500} r={5} fill='currentColor' opacity={0} />
                                </g>
                            ))}
                        </svg>
                    </div>

                    <div ref={copyRef} className='absolute inset-x-5 top-[26%] md:inset-x-auto md:bottom-[11%] md:left-[6vw] md:top-auto md:w-[28vw] md:max-w-[26rem]'>
                        <div data-global-item className='invisible opacity-0'>
                            <DestinationTicker />
                        </div>
                        <p data-global-item className='invisible mt-8 hidden font-heading text-[clamp(1.8rem,2.4vw,2.5rem)] font-normal leading-[1.1] opacity-0 md:block'>
                            {GLOBAL_COPY.title}
                        </p>
                        <p data-global-item className='invisible mt-5 hidden text-[14px] leading-[1.75] text-ivory/70 opacity-0 md:block'>
                            {GLOBAL_COPY.text}
                        </p>
                    </div>

                    <div ref={cardRef} className='invisible absolute inset-x-0 bottom-[13%] mx-auto w-[min(86vw,350px)] opacity-0 md:inset-x-auto md:bottom-[11%] md:right-[6vw] md:mx-0'>
                        <BoardingPass />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default GlobalSection
